import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ExternalLink, AlertTriangle, Download } from "lucide-react";

const MODE_LABEL = { retry: "Ontbrekende gegevens", personal: "Persoonlijke adressen", ids: "E-mails voor nieuwe leads" };

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg border border-slate-200 p-3">
    <div className="text-xs text-slate-500">{label}</div>
    <div data-testid={testid} className="text-2xl font-bold tabular-nums">{value}</div>
  </div>
);

const BatchTable = ({ log }) => (
  <div className="max-h-56 overflow-auto rounded-lg border border-slate-200">
    <table data-testid="report-batch-table" className="w-full text-sm">
      <thead className="bg-slate-50 text-slate-500 text-xs sticky top-0">
        <tr><th className="text-left p-2">Batch</th><th className="text-right p-2">Sites</th><th className="text-right p-2">Aangevuld</th>
          <th className="text-right p-2">Nieuwe e-mails</th><th className="text-right p-2">Mislukt</th><th className="text-right p-2">Duur</th></tr>
      </thead>
      <tbody>
        {log.map((b) => (
          <tr key={b.batch} data-testid={`report-batch-row-${b.batch}`} className="border-t border-slate-100">
            <td className="p-2">{b.batch}{b.error && <span className="ml-1 text-red-600 text-xs">fout</span>}</td>
            <td className="p-2 text-right tabular-nums">{b.sites}{b.requeued > 0 && <span className="ml-1 text-xs text-slate-400" title="Nog niet begonnen, doorgeschoven naar de volgende batch">+{b.requeued} door</span>}</td>
            <td className="p-2 text-right tabular-nums">{b.enriched}</td>
            <td className="p-2 text-right tabular-nums font-semibold text-emerald-700">{b.emails}</td>
            <td className="p-2 text-right tabular-nums text-amber-700">{b.failed}</td>
            <td className="p-2 text-right tabular-nums text-slate-500">{b.seconds}s</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const FailedList = ({ sites }) => (
  <div className="max-h-64 overflow-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
    {sites.map((s, i) => (
      <div key={`${s.id}-${i}`} data-testid={`report-failed-${s.id}`} className="flex items-center gap-3 p-2 text-sm">
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{s.name || s.website}</div>
          <div className="text-xs text-slate-500 truncate">{s.website} · {s.reason} · batch {s.batch}</div>
        </div>
        {/^https?:\/\//i.test(s.website || "") && <a data-testid={`report-open-${s.id}`} href={s.website} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 rounded-full border border-[#FF4500] text-[#FF4500] px-3 h-7 text-xs font-medium hover:bg-orange-50 transition-colors">
          <ExternalLink className="w-3.5 h-3.5" /> Open website
        </a>}
      </div>
    ))}
  </div>
);

export const ScrapeReport = ({ open, onOpenChange, job, onDownload }) => {
  if (!job) return null;
  const failed = job.failed_sites || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="scrape-report-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Scrape-rapport · {MODE_LABEL[job.mode] || "Diepe scrape"}</DialogTitle>
          <DialogDescription data-testid="report-stage">{job.stage}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Sites gescraped" value={`${job.processed}/${job.total}`} testid="report-total" />
          <Stat label="Records aangevuld" value={job.found_emails || 0} testid="report-enriched" />
          <Stat label="Nieuwe e-mails" value={job.new_emails || 0} testid="report-new-emails" />
          <Stat label="Mislukt" value={failed.length} testid="report-failed-count" />
        </div>
        {job.skipped > 0 && <p className="text-xs text-slate-500">{job.skipped} leads overgeslagen (al diep gescraped in de afgelopen 30 dagen).</p>}
        <h4 className="font-semibold text-sm mt-2">Per batch</h4>
        {(job.batch_log || []).length ? <BatchTable log={job.batch_log} /> : <p className="text-sm text-slate-500">Nog geen batches afgerond.</p>}
        <h4 className="font-semibold text-sm mt-2 flex items-center gap-1.5">
          <AlertTriangle className="w-4 h-4 text-amber-500" /> Mislukte sites ({failed.length})
          {failed.length > 0 && (
            <button data-testid="report-download-excel" onClick={onDownload}
              className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#FF4500] text-white px-3 h-7 text-xs font-medium hover:bg-[#E63E00] transition-colors">
              <Download className="w-3.5 h-3.5" /> Download als Excel
            </button>
          )}
        </h4>
        {failed.length ? <FailedList sites={failed} />
          : <p data-testid="report-no-failed" className="text-sm text-slate-500">Geen mislukte sites.</p>}
      </DialogContent>
    </Dialog>
  );
};
